import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, Button, Spinner } from '../components/ui';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id') || searchParams.get('txn_id');
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus('failed');
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sid}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      timeoutRef.current = setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      if (attempts < maxAttempts - 1) {
        timeoutRef.current = setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('failed');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full"
      >
        <Card className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
              <h1 className="font-heading text-2xl mb-2">Ödeme Kontrol Ediliyor</h1>
              <p className="text-muted-foreground">
                Lütfen bekleyin, ödemeniz doğrulanıyor...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
              >
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
              </motion.div>
              <h1 className="font-heading text-3xl mb-2 text-primary">Ödeme Başarılı!</h1>
              <p className="text-muted-foreground mb-6">
                Premium aboneliğiniz aktif edildi. Tüm özelliklerin keyfini çıkarın!
              </p>
              {paymentData && (
                <div className="bg-muted p-4 rounded-sm mb-6 text-left">
                  <div className="flex justify-between mb-2">
                    <span className="text-muted-foreground">Tutar:</span>
                    <span className="font-medium">${(paymentData.amount_total / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Durum:</span>
                    <span className="text-green-500 font-medium">Ödendi</span>
                  </div>
                </div>
              )}
              <Link to="/dashboard">
                <Button className="w-full">
                  Dashboard'a Git
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </>
          )}

          {(status === 'failed' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-destructive" />
              </div>
              <h1 className="font-heading text-2xl mb-2">Ödeme Başarısız</h1>
              <p className="text-muted-foreground mb-6">
                {status === 'timeout' 
                  ? 'Ödeme durumu kontrol edilemedi. Lütfen hesabınızı kontrol edin.'
                  : 'Ödeme işlemi tamamlanamadı. Lütfen tekrar deneyin.'}
              </p>
              <div className="flex gap-3">
                <Link to="/pricing" className="flex-1">
                  <Button variant="outline" className="w-full">
                    Tekrar Dene
                  </Button>
                </Link>
                <Link to="/dashboard" className="flex-1">
                  <Button className="w-full">
                    Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
